import { z } from 'zod'

export type ActivityDTO = {
	summary: Activity
	details: ActivityDetails
}

export type ActivityDetails = {
	context: {
		action_uuid: string
		adhoc?: boolean | null
		attempt_count?: number | null
		tenant_id: string
		time_scheduled: string
		time_started?: string | null
		time_completed?: string | null
		workflow_action_id: string
		workflow_instance_id: string
		workflow_name?: string
	}
	action_name: string
	action_output?: Record<string, any>
	error_info?: ActivityDetailsError | null
}

export type ActivityDetailsError = {
	error_code: string | null
	error_details: string | null
	error_message: string
	error_timestamp: string
}

export type Activity = {
	action_uuid?: string
	action_name?: string
	adhoc?: boolean
	canonical_id?: string
	display_name?: string
	status: ActivityStatus
	tenant?: string
	time_scheduled?: string
	time_started?: string
	time_completed?: string
	workflow_instance_id: string | null
	trigger_info?: {
		trigger_type: 'manual' | 'case' | 'workflow'
		workflow_instance_id: string
		workflow_name: string
		workflow_action_id: string
		case_id?: string
	}
}

/**
 * Status of a `soar_actions_activity` row — five members, deliberately NOT the six
 * of {@link WorkflowActionStatus} below.
 *
 * `waiting` is absent because nothing can write it here: only system actions emit
 * `waiting`, and the backend explicitly does not store system-action results in the
 * activity table. It lands on the workflow summary / instance instead. Widening this
 * union would make every exhaustive `Record<ActivityStatus, …>` cover a value that
 * cannot occur. See GOTCHAS, "`waiting` is a workflow-only status".
 */
const ACTION_ACTIVITY_STATUS = [
	'pending',
	'running',
	'cancelled',
	'success',
	'error',
] as const

export const ACTIVITY_STATUS = z.enum(ACTION_ACTIVITY_STATUS)

export type ActivityStatus = z.infer<typeof ACTIVITY_STATUS>

/**
 * Trigger information for automation actions
 */
export type TriggerInfo =
	SoarManualTrigger | SoarEventTrigger | SoarWorkflowTrigger

/**
 * Manual trigger initiated by a user
 */
export type SoarManualTrigger = {
	trigger_type: 'manual'
	username: string
	user_tenant_id: string
	user_tenant_name: string | null
}

/**
 * Event-based trigger
 */
export type SoarEventTrigger = {
	trigger_type: 'event'
	event_id: string
}

/**
 * Workflow-based trigger
 */
export type SoarWorkflowTrigger = {
	trigger_type: 'workflow'
	workflow_instance_id: string
	workflow_name: string
	workflow_action_id: string
}

/**
 * Status of a workflow action — six members, the backend `WorkflowActionStatus`.
 *
 * The extra member over {@link ActivityStatus} is `waiting`: a system action pausing
 * the playbook for human input. Real here, impossible on an activity row.
 */
const WorkflowStatus = [
	'pending',
	'waiting',
	'running',
	'cancelled',
	'error',
	'success',
] as const
export const WORKFLOW_ACTION_STATUS = z.enum(WorkflowStatus)
export type WorkflowActionStatus = z.infer<typeof WORKFLOW_ACTION_STATUS>

/**
 * Summary of a single workflow action
 */
export type WorkflowSummaryAction = {
	workflow_action_id: string
	action_name: string
	status: WorkflowActionStatus
	action_uuid: string
}

/**
 * Summary of all workflow actions
 */
export type WorkflowActionsSummary = {
	actions_summary: Array<WorkflowSummaryAction>
}

/**
 * Complete SOAR playbook summary
 */
export type SoarPlaybookSummary = {
	trigger_info: TriggerInfo
	actions_summary: WorkflowActionsSummary
	workflow_uuid: string
	tenant: string
	workflow_name: string
	time_created: string
	time_updated: string
	status: WorkflowActionStatus
}

/**
 * Response containing array of playbook summaries
 */
export type PlaybooksResponse = {
	playbooks: Array<SoarPlaybookSummary>
}
